import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { apiFetchJson } from '../../apiClient';

function StockSales({ sales, currency, exchangeRate, currentPrice, instance, onRefresh, selectedUserId }) {
  const { t } = useTranslation();
  const [showForm, setShowForm] = useState(false);
  const [fifoData, setFifoData] = useState(null);
  const [form, setForm] = useState({
    saleDate: '',
    sharesSold: '',
    salePricePerShareUSD: '',
    totalProceedsUSD: '',
    exchangeRate: '',
    notes: ''
  });

  const loadFifoSummary = useCallback(async () => {
    try {
      const url = selectedUserId
        ? `/api/StockSale/fifo-summary?userId=${selectedUserId}`
        : '/api/StockSale/fifo-summary';
      const data = await apiFetchJson(instance, url);
      setFifoData(data);
    } catch (err) {
      console.error('Failed to load FIFO summary:', err);
    }
  }, [instance, selectedUserId]);

  useEffect(() => {
    loadFifoSummary();
  }, [loadFifoSummary, sales]);

  const formatCurrency = (usd) => {
    if (usd == null) return '—';
    if (currency === 'NOK' && exchangeRate) {
      return `${(usd * exchangeRate).toLocaleString('nb-NO', { maximumFractionDigits: 0 })} kr`;
    }
    return `$${usd.toLocaleString('en-US', { maximumFractionDigits: 2 })}`;
  };

  const handleRefresh = () => {
    onRefresh();
    loadFifoSummary();
  };

  const updateShares = (val) => {
    const shares = parseFloat(val);
    const total = parseFloat(form.totalProceedsUSD);
    setForm(f => ({
      ...f,
      sharesSold: val,
      salePricePerShareUSD: shares > 0 && total > 0 ? (total / shares).toFixed(2) : f.salePricePerShareUSD
    }));
  };

  const updatePricePerShare = (val) => {
    const price = parseFloat(val);
    const shares = parseFloat(form.sharesSold);
    setForm(f => ({
      ...f,
      salePricePerShareUSD: val,
      totalProceedsUSD: price > 0 && shares > 0 ? (price * shares).toFixed(2) : f.totalProceedsUSD
    }));
  };

  const updateTotalProceeds = (val) => {
    const total = parseFloat(val);
    const shares = parseFloat(form.sharesSold);
    setForm(f => ({
      ...f,
      totalProceedsUSD: val,
      salePricePerShareUSD: total > 0 && shares > 0 ? (total / shares).toFixed(2) : f.salePricePerShareUSD
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await apiFetchJson(instance, '/api/StockSale', {
        method: 'POST',
        body: JSON.stringify({
          saleDate: form.saleDate,
          sharesSold: parseFloat(form.sharesSold),
          salePricePerShareUSD: parseFloat(form.salePricePerShareUSD),
          exchangeRate: form.exchangeRate ? parseFloat(form.exchangeRate) : null,
          notes: form.notes || null
        })
      });
      setShowForm(false);
      setForm({ saleDate: '', sharesSold: '', salePricePerShareUSD: '', totalProceedsUSD: '', exchangeRate: '', notes: '' });
      handleRefresh();
    } catch (err) {
      console.error('Failed to create stock sale:', err);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t('compensation.deleteSaleConfirm'))) return;
    try {
      await apiFetchJson(instance, `/api/StockSale/${id}`, { method: 'DELETE' });
      handleRefresh();
    } catch (err) {
      console.error('Failed to delete stock sale:', err);
    }
  };

  // Use server-side FIFO data
  const saleResults = Array.isArray(fifoData?.sales) ? fifoData.sales : (fifoData?.sales?.$values || []);
  const remainingShares = fifoData?.remainingShares || 0;
  const remainingCostBasis = fifoData?.remainingCostBasis || 0;
  const totalProceeds = fifoData?.totalProceeds || 0;
  const totalCostBasis = saleResults.reduce((sum, s) => sum + (s.costBasis || 0), 0);
  const totalGain = fifoData?.totalGain || 0;
  const totalSharesSold = fifoData?.totalSharesSold || 0;

  const currentHoldingValue = currentPrice ? remainingShares * currentPrice : null;
  const unrealizedGain = currentHoldingValue != null ? currentHoldingValue - remainingCostBasis : null;

  return (
    <div className="stock-sales">
      <div className="espp-header">
        <h3>💰 {t('compensation.stockSales')}</h3>
        <button className="btn-primary" onClick={() => setShowForm(!showForm)}>
          + {t('compensation.addSale')}
        </button>
      </div>

      {/* Holdings summary */}
      <div className="sales-summary">
        <div className="sales-summary-row">
          <div className="summary-card holdings-card">
            <h4>📊 {t('compensation.currentHoldings')}</h4>
            <div className="summary-stats">
              <div className="stat">
                <span className="stat-label">{t('compensation.sharesHeld')}</span>
                <span className="stat-value">{remainingShares.toFixed(2)}</span>
              </div>
              <div className="stat">
                <span className="stat-label">{t('compensation.costBasis')}</span>
                <span className="stat-value">{formatCurrency(remainingCostBasis)}</span>
              </div>
              {currentHoldingValue != null && (
                <div className="stat">
                  <span className="stat-label">{t('compensation.currentValue')}</span>
                  <span className="stat-value">{formatCurrency(currentHoldingValue)}</span>
                </div>
              )}
              {unrealizedGain != null && (
                <div className="stat" style={{ color: unrealizedGain >= 0 ? 'var(--success-500)' : 'var(--danger-500)' }}>
                  <span className="stat-label">{t('compensation.unrealizedGain')}</span>
                  <span className="stat-value">
                    {unrealizedGain >= 0 ? '+' : ''}{formatCurrency(unrealizedGain)}
                    {remainingCostBasis > 0 && ` (${unrealizedGain >= 0 ? '+' : ''}${((unrealizedGain / remainingCostBasis) * 100).toFixed(1)}%)`}
                  </span>
                </div>
              )}
            </div>
          </div>

          {saleResults.length > 0 && (
            <div className="summary-card realized-card">
              <h4>💵 {t('compensation.realizedGains')}</h4>
              <div className="summary-stats">
                <div className="stat">
                  <span className="stat-label">{t('compensation.sharesSold')}</span>
                  <span className="stat-value">{totalSharesSold.toFixed(2)}</span>
                </div>
                <div className="stat">
                  <span className="stat-label">{t('compensation.totalProceeds')}</span>
                  <span className="stat-value">{formatCurrency(totalProceeds)}</span>
                </div>
                <div className="stat" style={{ color: totalGain >= 0 ? 'var(--success-500)' : 'var(--danger-500)' }}>
                  <span className="stat-label">{t('compensation.realizedGain')}</span>
                  <span className="stat-value">
                    {totalGain >= 0 ? '+' : ''}{formatCurrency(totalGain)}
                    {totalCostBasis > 0 && ` (${totalGain >= 0 ? '+' : ''}${((totalGain / totalCostBasis) * 100).toFixed(1)}%)`}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Add sale form */}
      {showForm && (
        <form className="espp-form" onSubmit={handleSubmit}>
          <div className="form-grid">
            <div className="form-group">
              <label>{t('compensation.saleDate')}</label>
              <input type="date" value={form.saleDate}
                onChange={e => setForm(f => ({ ...f, saleDate: e.target.value }))} required />
            </div>
            <div className="form-group">
              <label>{t('compensation.sharesSold')}</label>
              <input type="number" value={form.sharesSold}
                onChange={e => updateShares(e.target.value)}
                placeholder="e.g. 20" step="0.0001" required />
            </div>
            <div className="form-group">
              <label>{t('compensation.totalProceeds')} (USD)</label>
              <input type="number" value={form.totalProceedsUSD}
                onChange={e => updateTotalProceeds(e.target.value)}
                placeholder="e.g. 7400.00" step="0.01" />
            </div>
            <div className="form-group">
              <label>{t('compensation.salePriceUSD')}</label>
              <input type="number" value={form.salePricePerShareUSD}
                onChange={e => updatePricePerShare(e.target.value)}
                placeholder="e.g. 370.00" step="0.01" required />
            </div>
            <div className="form-group">
              <label>USD/NOK</label>
              <input type="number" value={form.exchangeRate}
                onChange={e => setForm(f => ({ ...f, exchangeRate: e.target.value }))}
                placeholder={t('compensation.optional')} step="0.01" />
            </div>
            <div className="form-group">
              <label>{t('compensation.notes')}</label>
              <input type="text" value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
                placeholder={t('compensation.optional')} />
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">{t('common.save')}</button>
            <button type="button" className="btn-secondary" onClick={() => setShowForm(false)}>{t('common.cancel')}</button>
          </div>
        </form>
      )}

      {/* Sale history with FIFO breakdown */}
      {saleResults.length > 0 && (
        <div className="espp-table-container">
          <h4>{t('compensation.saleHistory')}</h4>
          <table className="espp-table">
            <thead>
              <tr>
                <th>{t('compensation.date')}</th>
                <th>{t('compensation.shares')}</th>
                <th>{t('compensation.salePriceUSD')}</th>
                <th>{t('compensation.totalProceeds')}</th>
                <th>{t('compensation.costBasis')} (FIFO)</th>
                <th>{t('compensation.realizedGain')}</th>
                <th>{t('compensation.notes')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {saleResults.map(s => (
                <tr key={s.id}>
                  <td>{new Date(s.saleDate).toLocaleDateString('nb-NO')}</td>
                  <td>{s.sharesSold?.toFixed(4)}</td>
                  <td>${s.salePricePerShareUSD?.toFixed(2)}</td>
                  <td>{formatCurrency(s.proceeds)}</td>
                  <td>{formatCurrency(s.costBasis)}</td>
                  <td style={{ color: s.gain >= 0 ? 'var(--success-500)' : 'var(--danger-500)', fontWeight: 600 }}>
                    {s.gain >= 0 ? '+' : ''}{formatCurrency(s.gain)}
                    <span className="gain-pct"> ({s.gainPercent >= 0 ? '+' : ''}{s.gainPercent.toFixed(1)}%)</span>
                  </td>
                  <td className="notes-cell">{s.notes || ''}</td>
                  <td>
                    <button className="btn-icon" onClick={() => handleDelete(s.id)}>🗑️</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {saleResults.length === 0 && !showForm && (
        <p className="empty-message">{t('compensation.noSales')}</p>
      )}
    </div>
  );
}

export default StockSales;
